import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../common/Logo';
import GooglePlayIcon from '../common/GooglePlayIcon';
import { USER_APP_URL } from '../../config/appLinks';
import { WEB_BOOKING_ENABLED } from '../../config/env';
import '../../styles/route.css';
import { clearAdminAuthFlow, getAdminAuthUser } from '../../services/storage/adminFlow';
import { logoutAdminAuthSession } from '../../services/api/admin';
import {
  clearCompanyOwnerAuthFlow,
  getCompanyOwnerAuthUser,
  getCompanyOwnerCompany,
} from '../../services/storage/companyOwnerFlow';
import { logoutCompanyOwnerAuthSession } from '../../services/api/companyOwnerAuth';
import { clearCustomerAuthFlow, getCustomerAuthUser } from '../../services/storage/customerFlow';
import { logoutCustomerAuthSession } from '../../services/api/customerAuth';
import { clearDriverAuthFlow, getDriverAuthUser } from '../../services/storage/driverFlow';
import { logoutDriverAuthSession } from '../../services/api/driverAuth';
import { getCompanyOwnerNextRoute } from '../../utils/companyOwner';
import { nextStageForAdmin } from '../../utils/adminOnboarding';
import { nextStageForCustomer } from '../../utils/customerOnboarding';
import { nextStageForDriver } from '../../utils/driverOnboarding';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const customerUser = getCustomerAuthUser();
  const driverUser = getDriverAuthUser();
  const adminUser = getAdminAuthUser();
  const companyOwnerUser = getCompanyOwnerAuthUser();
  const company = getCompanyOwnerCompany();
  const activeRole = adminUser
    ? 'admin'
    : companyOwnerUser
    ? 'company'
    : driverUser
      ? 'driver'
      : customerUser
        ? 'customer'
        : null;

  const primaryLink = useMemo(() => {
    if (activeRole === 'company' && companyOwnerUser) {
      const nextRoute = getCompanyOwnerNextRoute(company);
      return {
        to: nextRoute,
        label:
          nextRoute === '/company/payout-setup'
            ? 'Payout Setup'
            : company
              ? 'Company Dashboard'
              : 'Company Setup',
      };
    }

    if (activeRole === 'admin' && adminUser) {
      const nextStage = nextStageForAdmin(adminUser);
      return nextStage === 'completed'
        ? { to: '/admin', label: 'Admin Dashboard' }
        : { to: '/admin/onboarding', label: 'Complete Setup' };
    }

    if (activeRole === 'driver' && driverUser) {
      const nextStage = nextStageForDriver(driverUser);
      return nextStage === 'completed'
        ? { to: '/driver/account-ready', label: 'Driver Ready' }
        : { to: '/register?resume=header&role=driver', label: 'Complete Setup' };
    }

    if (activeRole === 'customer' && customerUser) {
      const nextStage = nextStageForCustomer(customerUser);

      if (nextStage !== 'completed') {
        return { to: '/register?resume=header&role=customer', label: 'Complete Setup' };
      }

      /* Once the customer area exists, a finished customer has somewhere real
         to go and the label can say so. While the flag is off they are sent to
         /schedule-booking, which exists to say web booking is not live — and
         the label must name where booking actually happens, because
         "Schedule Booking" promised a web flow the destination then denies. */
      return WEB_BOOKING_ENABLED
        ? { to: '/account', label: 'Your deliveries' }
        : { to: '/schedule-booking', label: 'Book in the app' };
    }

    return null;
  }, [activeRole, adminUser, company, companyOwnerUser, customerUser, driverUser]);

  /**
   * "Book a delivery" — the standing action for a signed-in customer.
   *
   * Separate from `primaryLink` because a customer needs both: somewhere to
   * watch what is moving, and somewhere to start something new. Collapsing them
   * into one slot is what left booking reachable only from the homepage.
   *
   * The target is the homepage estimate widget, NOT /book/confirm. Confirm
   * requires a priced draft held in memory by `BookingDraftProvider`; reached
   * cold it has nothing to confirm. #estimate is where a route actually gets
   * priced, so it is the real start of the flow.
   *
   * Gated on the same `completed` stage as `primaryLink`: an account still
   * mid-verification cannot finish a booking, and offering one would be a
   * promise the confirm step breaks.
   */
  const bookingLink = useMemo(() => {
    if (!WEB_BOOKING_ENABLED || activeRole !== 'customer' || !customerUser) {
      return null;
    }

    return nextStageForCustomer(customerUser) === 'completed' ? '/#estimate' : null;
  }, [activeRole, customerUser]);

  const handleLogout = async () => {
    try {
      if (activeRole === 'admin') await logoutAdminAuthSession();
      if (activeRole === 'company') await logoutCompanyOwnerAuthSession();
      if (activeRole === 'driver') await logoutDriverAuthSession();
      if (activeRole === 'customer') await logoutCustomerAuthSession();
    } catch {
      // Local logout should still win if the network is unavailable.
    } finally {
      clearAdminAuthFlow();
      clearCustomerAuthFlow();
      clearDriverAuthFlow();
      clearCompanyOwnerAuthFlow();
    }
    navigate('/');
  };

  const closeNav = () => setNavOpen(false);

  return (
    <header className="landing-header">
      <div className="container landing-header-inner">
        <Link className="landing-brand" to="/" onClick={closeNav} aria-label="ShiftWagon home">
          <Logo size={36} />
        </Link>

        {/* Labelled disclosure, not an unlabelled hamburger. Mobile only. */}
        <button
          className="sw-nav-toggle"
          type="button"
          aria-expanded={navOpen}
          aria-controls="sw-primary-nav"
          onClick={() => setNavOpen((open) => !open)}
        >
          {navOpen ? 'Close' : 'Menu'}
        </button>

        <nav
          className={`landing-header-nav${navOpen ? ' is-open' : ''}`}
          id="sw-primary-nav"
          aria-label="Primary"
        >
          <Link className="landing-header-link" to="/how-it-works" onClick={closeNav}>
            How it works
          </Link>
          <Link className="landing-header-link" to="/driver-onboarding-guide" onClick={closeNav}>
            Driver guide
          </Link>
          <Link className="landing-header-link" to="/#vehicles" onClick={closeNav}>
            Vehicles
          </Link>
          <Link className="landing-header-link" to="/about" onClick={closeNav}>
            About
          </Link>

          {primaryLink ? (
            <>
              {/* Booking first: it is the reason someone signs in. Plain nav
                  link rather than a filled button so the header never competes
                  with the page's one Signal Yellow action (design-system rule 1). */}
              {bookingLink ? (
                <Link className="landing-header-link" to={bookingLink} onClick={closeNav}>
                  Book a delivery
                </Link>
              ) : null}
              <Link className="landing-header-link" to={primaryLink.to} onClick={closeNav}>
                {primaryLink.label}
              </Link>
              <button
                className="sw-btn-secondary"
                onClick={() => {
                  closeNav();
                  handleLogout();
                }}
                type="button"
              >
                Log out
              </button>
            </>
          ) : (
            <>
              <Link className="landing-header-link" to="/login" onClick={closeNav}>
                Log in
              </Link>
              {/* Booking happens in the app — web booking is still "coming
                  soon" — so the nav's standing action is the download, not a
                  web "book" flow that doesn't exist.
                  Outlined variant so it never competes with the page's one
                  Signal Yellow action (see design-system rule 1). */}
              <a
                className="sw-btn-secondary sw-btn-compact"
                href={USER_APP_URL}
                onClick={closeNav}
                rel="noreferrer"
                target="_blank"
              >
                <GooglePlayIcon />
                Google Play
              </a>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
